import { useState, type ReactNode } from 'react'
import { LocalizedDecimalInput } from '../../shared/i18n/LocalizedInputs'
import { useLocale } from '../../shared/i18n/LocaleContext'
import type { ArticleItem, MaterialSnapshot } from './types'
import { useIntegrationProtection } from '../../shared/integrations/useIntegrationProtection'

export function ArticleEditorForm({ item, data, save }: {
  item: ArticleItem
  data: MaterialSnapshot
  save: (body: unknown) => void
}) {
  const { t, locale } = useLocale()
  const protection = useIntegrationProtection('Material')
  const managed = protection.isProtected
  const [form, setForm] = useState({
    name: item.name, description: item.description, type: item.type,
    articleTypeDefinitionId: item.articleTypeDefinitionId ?? '',
    procurementType: item.procurementType, familyId: item.familyId ?? '',
    categoryId: item.categoryId ?? '', baseUnitId: item.baseUnitId ?? '',
    revision: item.revision, inventoryTracked:item.inventoryTracked,
    lotTracked:item.lotTracked, serialTracked:item.serialTracked,
    leadTimeDays: item.leadTimeDays,
    safetyStock: item.safetyStock, reorderPoint: item.reorderPoint,
    minimumOrderQuantity: item.minimumOrderQuantity,
    orderMultiple: item.orderMultiple,
    maximumOrderQuantity: item.maximumOrderQuantity ?? 0,
    isActive: item.isActive,
  })
  const decimal = (key: 'leadTimeDays'|'safetyStock'|'reorderPoint'|'minimumOrderQuantity'|'orderMultiple'|'maximumOrderQuantity') =>
    <LocalizedDecimalInput locale={locale} value={form[key]} min={0}
      disabled={managed(key)}
      onChange={value => setForm(current => ({ ...current, [key]: value }))} />

  return <form className="administration-form record-form" onSubmit={event => {
    event.preventDefault()
    save({ ...form, code: null, familyId: form.familyId || null,
      categoryId: form.categoryId || null, baseUnitId: form.baseUnitId || null,
      maximumOrderQuantity: form.maximumOrderQuantity || null })
  }}>
    <div className="compact-form-grid">
      <Field label={t('aplPanel.code')} managed={managed('code')}><input disabled value={item.code} /></Field>
      <Field label={t('aplPanel.name')} managed={managed('name')}><input required disabled={managed('name')} value={form.name}
        onChange={event => setForm({ ...form, name: event.target.value })} /></Field>
      <Field label={t('materials.articleType')} managed={managed('articleType')}><select required disabled={managed('articleType')} value={form.articleTypeDefinitionId}
        onChange={event => { const selected=data.articleTypes.find(value=>value.id===event.target.value); setForm({
          ...form, articleTypeDefinitionId:event.target.value,
          procurementType:selected?.procurementType??form.procurementType,
        }) }}>
        <option value="">—</option>{data.articleTypes.filter(value=>value.isActive||value.id===item.articleTypeDefinitionId).map(value =>
          <option key={value.id} value={value.id}>{value.code} · {value.name}</option>)}
      </select></Field>
      <Field label={t('materials.procurement')} managed={managed('procurementType')}><select disabled value={form.procurementType}>
        {['Manufacture','Purchase','Transfer','Subcontract','Other'].map(value =>
          <option key={value} value={value}>{t(`materials.procurement.${value}` as Parameters<typeof t>[0])}</option>)}
      </select></Field>
      <Field label={t('materials.unit')} managed={managed('baseUnitCode')}><select disabled={managed('baseUnitCode')} value={form.baseUnitId}
        onChange={event => setForm({ ...form, baseUnitId: event.target.value })}><option value="">—</option>
        {data.units.filter(value => value.isActive || value.id === item.baseUnitId).map(value =>
          <option value={value.id} key={value.id}>{value.code} · {value.symbol}</option>)}</select></Field>
      <Field label={t('materials.family')} managed={managed('familyCode')}><select disabled={managed('familyCode')} value={form.familyId}
        onChange={event => setForm({ ...form, familyId: event.target.value })}><option value="">—</option>
        {data.families.map(value => <option value={value.id} key={value.id}>{value.name}</option>)}</select></Field>
      <Field label={t('materials.category')} managed={managed('categoryCode')}><select disabled={managed('categoryCode')} value={form.categoryId}
        onChange={event => setForm({ ...form, categoryId: event.target.value })}><option value="">—</option>
        {data.categories.map(value => <option value={value.id} key={value.id}>{value.name}</option>)}</select></Field>
      <Field label={t('materials.revision')} managed={managed('revision')}><input disabled={managed('revision')} value={form.revision}
        onChange={event => setForm({ ...form, revision: event.target.value })} /></Field>
      <Field label={t('materials.description')} managed={managed('description')}><textarea disabled={managed('description')} value={form.description}
        onChange={event => setForm({ ...form, description: event.target.value })} /></Field>
      <Field label={t('materials.leadTime')} managed={managed('leadTimeDays')}>{decimal('leadTimeDays')}</Field>
    </div>
    <div className="materials-check-grid">
      <Check label={t('materials.inventoryTracked')} disabled={managed('inventoryTracked')} value={form.inventoryTracked} set={value => setForm({ ...form, inventoryTracked: value })} />
      <Check label={t('materials.lotTracked')} disabled={managed('lotTracked')} value={form.lotTracked} set={value => setForm({ ...form, lotTracked: value })} />
      <Check label={t('materials.serialTracked')} disabled={managed('serialTracked')} value={form.serialTracked} set={value => setForm({ ...form, serialTracked: value })} />
      <Check label={t('administration.active')} disabled={managed('isActive')} value={form.isActive} set={value => setForm({ ...form, isActive: value })} />
    </div>
    <div className="form-actions"><button className="primary-button" type="submit">{t('administration.save')}</button></div>
  </form>
}

function Field({ label, children, managed = false }: { label: string; children: ReactNode; managed?: boolean }) {
  const { t } = useLocale()
  return <label className={managed ? 'integration-managed-field' : undefined}><span>{label}</span>{children}
    {managed && <small>{t('integrator.managedField')}</small>}</label>
}
function Check({ label, value, set, disabled = false }: { label: string; value: boolean; set: (value: boolean) => void; disabled?: boolean }) {
  return <label className={`checkbox-row${disabled ? ' integration-managed-field' : ''}`}><input type="checkbox" disabled={disabled} checked={value}
    onChange={event => set(event.target.checked)} /><span>{label}</span></label>
}
